// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
/**
 * The production seams for press-to-talk, and the development speech-to-text stand-in.
 *
 * These are deliberately thin. Everything worth testing lives in `createPressToTalk`; what is here is
 * the pass-through to the browser plus one honest fake, so that a local run can exercise the whole
 * capture path with no credential and no provider.
 */
import type {
  CaptureSeams,
  MicrophoneStreamLike,
  RecorderLike,
  SttClient,
} from './press-to-talk.js';

/** The two halves capture needs. Missing either means no microphone control is offered at all. */
export function supportsCapture(navigatorLike: {
  readonly MediaRecorder?: unknown;
  readonly mediaDevices?: unknown;
}): boolean {
  return navigatorLike.MediaRecorder !== undefined && navigatorLike.mediaDevices !== undefined;
}

/** Long enough for a permission prompt a person is reading, short enough to not look frozen. */
export const MICROPHONE_TIMEOUT_MS = 8_000;

export interface BrowserCaptureInput<Stream extends MicrophoneStreamLike> {
  readonly stt: SttClient;
  readonly mediaDevices: {
    getUserMedia(constraints: {
      readonly audio: boolean;
      readonly video: boolean;
    }): Promise<Stream>;
  };
  readonly recorderFor: (stream: Stream) => RecorderLike;
}

export function browserCaptureSeams<Stream extends MicrophoneStreamLike>(
  input: BrowserCaptureInput<Stream>,
): CaptureSeams<Stream> {
  return {
    // Audio only, stated rather than defaulted: a capture control must never open a camera.
    openMicrophone: () => input.mediaDevices.getUserMedia({ audio: true, video: false }),
    createRecorder: (stream) => input.recorderFor(stream),
    stt: input.stt,
    microphoneTimeoutMs: MICROPHONE_TIMEOUT_MS,
    delay: (ms) =>
      new Promise<void>((resolve) => {
        globalThis.setTimeout(resolve, ms);
      }),
  };
}

/**
 * Speech-to-text for the offline development build. It transcribes nothing: it reports, in words, that
 * a recording of a given size arrived. Inventing plausible Arabic for the audio would make a local run
 * look like a working product and hide the fact that no provider is wired.
 *
 * The real client exchanges a backend-minted single-use token with the provider directly (queue item F,
 * `POST /v1/sessions/{sessionId}/stt-token`) and needs a credential this build does not have.
 */
export function developmentSttClient(): SttClient {
  return {
    transcribe: ({ audio }) =>
      Promise.resolve(
        audio.size === 0
          ? { kind: 'unavailable', reason: 'unintelligible' }
          : {
              kind: 'transcribed',
              text: `Synthetic development transcript of a ${String(audio.size)}-byte recording — no provider is wired.`,
              confidence: 1,
            },
      ),
  };
}
